/**
 * テーブル計算・判定処理のComposable
 * Store内の計算ロジックを純粋関数として提供
 */

import { MonthlyData, IMonthlyBusinessData } from '../domains/entities/MonthlyData';
import { ArrivalStatus } from '@/coreDomainsModule/entities/arrival';
import { DateService } from '@/coreDomainsModule/services/dateService';

/**
 * 月次データキャッシュのキー生成インターフェース
 */
interface CacheKey {
  businessId: string;
  month: number;
  // データの変更検出用ハッシュ（将来的な拡張用）
  dataVersion?: string;
}

/**
 * キャッシュされた月次データ
 */
interface CachedMonthlyData {
  data: MonthlyData;
  timestamp: number;
  version: string;
}

// #region キャッシュ管理
/**
 * 月次データのキャッシュストレージ
 * WeakMapを使用してメモリリークを防止
 */
const monthlyDataCache = new WeakMap<Record<string, IMonthlyBusinessData>, Map<string, CachedMonthlyData>>();

/**
 * キャッシュキーを生成する
 * @param businessId - ビジネスID
 * @param month - 対象月
 * @returns キャッシュキー文字列
 */
const generateCacheKey = (businessId: string, month: number): string => {
  return `${businessId}-${month}`;
};

/**
 * データのバージョンハッシュを生成する（将来的な拡張用）
 * @param businessData - 月次ビジネスデータ
 * @returns バージョンハッシュ
 */
const generateDataVersion = (businessData: IMonthlyBusinessData): string => {
  // 簡易的なハッシュ生成（本格的な実装では適切なハッシュ関数を使用）
  const dataString = JSON.stringify(businessData);
  return btoa(dataString).slice(0, 16);
};

/**
 * キャッシュからデータを取得する
 * @param monthlyDataCacheRef - 月次データキャッシュ
 * @param businessId - ビジネスID
 * @param month - 対象月
 * @returns キャッシュされたデータまたはundefined
 */
const getCachedData = (
  monthlyDataCacheRef: Record<string, IMonthlyBusinessData>,
  businessId: string,
  month: number
): MonthlyData | undefined => {
  const cacheMap = monthlyDataCache.get(monthlyDataCacheRef);
  if (!cacheMap) {
    return undefined;
  }

  const cacheKey = generateCacheKey(businessId, month);
  const cachedItem = cacheMap.get(cacheKey);

  if (!cachedItem) {
    return undefined;
  }

  // キャッシュの有効性をチェック（データバージョンの比較）
  const currentBusinessData = monthlyDataCacheRef[businessId];
  if (currentBusinessData) {
    const currentVersion = generateDataVersion(currentBusinessData);
    if (cachedItem.version !== currentVersion) {
      // データが変更されているためキャッシュを無効化
      cacheMap.delete(cacheKey);
      return undefined;
    }
  }

  return cachedItem.data;
};

/**
 * データをキャッシュに保存する
 * @param monthlyDataCacheRef - 月次データキャッシュ
 * @param businessId - ビジネスID
 * @param month - 対象月
 * @param data - 保存するデータ
 * @param version - データのバージョン
 */
const setCachedData = (
  monthlyDataCacheRef: Record<string, IMonthlyBusinessData>,
  businessId: string,
  month: number,
  data: MonthlyData,
  version: string
): void => {
  let cacheMap = monthlyDataCache.get(monthlyDataCacheRef);
  if (!cacheMap) {
    cacheMap = new Map<string, CachedMonthlyData>();
    monthlyDataCache.set(monthlyDataCacheRef, cacheMap);
  }

  const cacheKey = generateCacheKey(businessId, month);
  cacheMap.set(cacheKey, {
    data,
    timestamp: Date.now(),
    version,
  });
};

/**
 * キャッシュをクリアする（メモリ管理用）
 * @param monthlyDataCacheRef - 月次データキャッシュ
 */
const clearCache = (monthlyDataCacheRef: Record<string, IMonthlyBusinessData>): void => {
  const cacheMap = monthlyDataCache.get(monthlyDataCacheRef);
  if (cacheMap) {
    cacheMap.clear();
  }
};
// #endregion

/**
 * 月次データ取得の引数インターフェース
 */
interface GetMonthlyDataParams {
  /** 月次データキャッシュ */
  monthlyDataCache: Record<string, IMonthlyBusinessData>;
  /** ビジネスID */
  businessId: string;
  /** 対象月 */
  month: number;
}

/**
 * テーブル計算機能を提供するComposable
 */
export const useTableCalculation = () => {
  /**
   * 月次データを取得する（最適化版）
   * @param monthlyDataCacheRef - 月次データキャッシュ
   * @param businessId - ビジネスID
   * @param month - 対象月（MonthDistanceに対応）
   * @returns 月次データオブジェクト（データが見つからない場合は空のMonthlyDataオブジェクト）
   */
  const getMonthlyData = (monthlyDataCacheRef: Record<string, IMonthlyBusinessData>, businessId: string, month: number): MonthlyData => {
    // 引数の妥当性チェック
    if (!businessId || typeof businessId !== 'string') {
      return {} as MonthlyData;
    }

    // ビジネスIDに対応するデータが存在しない場合
    if (monthlyDataCacheRef[businessId] === undefined) {
      return {} as MonthlyData;
    }

    // 直接データを返す（キャッシュなし）
    const businessData = monthlyDataCacheRef[businessId];
    return businessData[month] || ({} as MonthlyData);
  };

  /**
   * その月に最も近い推奨発注かどうかを判定する
   * Store内のcheckNearestRecommend関数と同等の機能を提供
   * @description
   * 最も近い推奨発注の月のみTagを表示するための判定を行う。
   * 当月の場合は、当月以前に推奨発注があるかどうかを確認する特別なロジックを適用。
   * @param monthlyDataCache - 月次データキャッシュ
   * @param businessId - ビジネスID
   * @param month - 対象月（MonthDistanceに対応、0=当月、正の値=未来、負の値=過去）
   * @returns 判定結果（true: 最も近い推奨発注, false: そうでない）
   * @example
   * ```typescript
   * // 当月が最も近い推奨発注かどうかを判定
   * const isNearest = checkNearestRecommend(state.monthlyData, 'business-123', 0);
   * if (isNearest) {
   *   console.log('推奨発注タグを表示');
   * }
   * ```
   */
  const checkNearestRecommend = (monthlyDataCache: Record<string, IMonthlyBusinessData>, businessId: string, month: number): boolean => {
    // 引数の妥当性チェック
    if (!businessId || typeof businessId !== 'string') {
      return false;
    }

    // ビジネスIDに対応するデータが存在しない場合
    if (!monthlyDataCache[businessId]) {
      return false;
    }

    // 最も近い推奨発注の日付を取得
    const nearestRecommend = monthlyDataCache[businessId]?.calcNearestRecommend?.crossDate;
    if (nearestRecommend === undefined) {
      return false;
    }

    // 対象月の日付を計算
    const targetMonth = DateService.addMonthsToCurrentDate(month);

    // 当月の場合は、当月以前に推奨発注があるかどうかを確認する
    if (month === 0 && DateService.compareDates(nearestRecommend, targetMonth) <= 0) {
      return true;
    }

    // 年月が一致する場合は最も近い推奨発注
    if (DateService.compareYearMonths(nearestRecommend, targetMonth) === 0) {
      return true;
    }

    return false;
  };

  /**
   * ボタン表示時の到着ステータスを取得する
   * Store内のgetArrivalStatusWhenDisplayButton関数と同等の機能を提供
   * @description
   * MonthlyDataから到着ステータスリストを取得し、優先度に基づいて表示すべきステータスを決定する。
   * 優先度: Unpaid > ApprovalRequested > Recommended > Nothing
   * @param monthlyDataCache - 月次データキャッシュ
   * @param businessId - ビジネスID
   * @param month - 対象月（MonthDistanceに対応）
   * @returns ボタン表示用のArrivalStatus
   * @example
   * ```typescript
   * // ボタンに表示するステータスを取得
   * const status = getArrivalStatusWhenDisplayButton(state.monthlyData, 'business-123', 0);
   * if (status === ArrivalStatus.Unpaid) {
   *   console.log('未納ボタンを表示');
   * }
   * ```
   */
  const getArrivalStatusWhenDisplayButton = (
    monthlyDataCache: Record<string, IMonthlyBusinessData>,
    businessId: string,
    month: number
  ): ArrivalStatus => {
    // 引数の妥当性チェック
    if (!businessId || typeof businessId !== 'string') {
      return ArrivalStatus.Nothing;
    }

    // getMonthlyDataを使用して月次データを取得（エラーハンドリング込み）
    const monthlyData = getMonthlyData(monthlyDataCache, businessId, month);

    // 月次データが空の場合（getMonthlyDataが{}を返した場合）
    if (Object.keys(monthlyData).length === 0) {
      return ArrivalStatus.Nothing;
    }

    // arrivalStatusListが存在しない場合
    if (monthlyData.arrivalStatusList === undefined || !Array.isArray(monthlyData.arrivalStatusList)) {
      return ArrivalStatus.Nothing;
    }

    // 優先度順にステータスを判定
    // 1. 最優先: Unpaid（未納）
    if (monthlyData.arrivalStatusList.includes(ArrivalStatus.Unpaid)) {
      return ArrivalStatus.Unpaid;
    }

    // 2. 次優先: ApprovalRequested（承認依頼中）
    if (monthlyData.arrivalStatusList.includes(ArrivalStatus.ApprovalRequested)) {
      return ArrivalStatus.ApprovalRequested;
    }

    // 3. デフォルト: Recommended（推奨）
    return ArrivalStatus.Recommended;
  };

  /**
   * 非表示ヘッダー数を計算する
   * Store内のgetHiddenHeaderNumber関数と同等の機能を提供
   * @description
   * 全てのヘッダーリストと現在表示中のヘッダーリストを比較し、
   * 非表示になっているヘッダーの数を計算する。
   * @param allHeaders - 全てのヘッダー配列（vm.displayHeaders.inventoryControlに相当）
   * @param visibleHeaders - 表示中のヘッダー配列（state.headerListに相当）
   * @returns 非表示ヘッダー数
   * @example
   * ```typescript
   * const allHeaders = ['header1', 'header2', 'header3', 'header4'];
   * const visibleHeaders = ['header1', 'header3'];
   * const hiddenCount = getHiddenHeaderNumber(allHeaders, visibleHeaders);
   * console.log(hiddenCount); // 2 (header2とheader4が非表示)
   * ```
   */
  const getHiddenHeaderNumber = (allHeaders: string[], visibleHeaders: string[]): number => {
    // 引数の妥当性チェック
    if (!Array.isArray(allHeaders) || !Array.isArray(visibleHeaders)) {
      return 0;
    }

    // 空の配列の場合のハンドリング
    if (allHeaders.length === 0) {
      return 0;
    }

    // 非表示ヘッダーを特定して数を計算
    const hiddenHeaders = allHeaders.filter((header) => !visibleHeaders.includes(header));

    return hiddenHeaders.length;
  };

  /**
   * 左側の非表示ヘッダー数を計算する
   * Store内のgetLeftSideHiddenHeaderNumber関数と同等の機能を提供
   * @description
   * 指定されたヘッダーより左側にある非表示ヘッダーの数を計算する。
   * ヘッダーの順序は固定されており、左から右へ順番に処理される。
   * @param allHeaders - 全てのヘッダー配列（vm.displayHeaders.inventoryControlに相当）
   * @param visibleHeaders - 表示中のヘッダー配列（state.headerListに相当）
   * @param targetHeader - 基準となるヘッダー（このヘッダーより左側を計算）
   * @returns 左側の非表示ヘッダー数
   * @example
   * ```typescript
   * const allHeaders = ['header1', 'header2', 'header3', 'header4'];
   * const visibleHeaders = ['header1', 'header4'];
   * const leftHiddenCount = getLeftSideHiddenHeaderNumber(allHeaders, visibleHeaders, 'header4');
   * // header4より左側にある非表示ヘッダー: header2, header3 -> 2
   * ```
   */
  const getLeftSideHiddenHeaderNumber = (allHeaders: string[], visibleHeaders: string[], targetHeader: string): number => {
    // 引数の妥当性チェック
    if (!Array.isArray(allHeaders) || !Array.isArray(visibleHeaders) || typeof targetHeader !== 'string') {
      return 0;
    }

    // 空の配列や空の文字列の場合のハンドリング
    if (allHeaders.length === 0 || !targetHeader.trim()) {
      return 0;
    }

    // 基準ヘッダーが存在しない場合
    const targetIndex = allHeaders.indexOf(targetHeader);
    if (targetIndex === -1) {
      return 0;
    }

    // 基準ヘッダーより左側のヘッダーを取得
    const leftSideHeaders = allHeaders.slice(0, targetIndex);

    // 左側のヘッダーの中で非表示になっているものを計算
    const leftSideHiddenHeaders = leftSideHeaders.filter((header) => !visibleHeaders.includes(header));

    return leftSideHiddenHeaders.length;
  };

  // 公開する関数
  return {
    getMonthlyData,
    checkNearestRecommend,
    getArrivalStatusWhenDisplayButton,
    getHiddenHeaderNumber,
    getLeftSideHiddenHeaderNumber,
  };
};

/**
 * 型定義のエクスポート（他のファイルで使用可能）
 */
export type { GetMonthlyDataParams };
